import {
  Stack,
  TextField,
  Autocomplete,
} from '@mui/material';
import { type FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { useMutateCreateCodeMetaData, useMutateUpdateCodeMetaData } from '../../api/index.js';
import { type CodeMetadata } from '../../models/index.js';
import { Events, humanize, track } from '../../utils/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import FormDialog from '../Forms/FormDialog.js';
import { type CodeMetadataFormValues } from '../Forms/formTypes.js';
import { codeMetadataFormSchema } from '../Forms/validations.js';

const INPUT_TYPE_OPTIONS = ['text', 'textarea'];

const defaultValues: CodeMetadataFormValues = {
  prompt: '',
  inputType: 'text',
};

interface CodeMetadataFormProps {
  codeMetadata?: CodeMetadata;
  codeId?: string;
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}

const CodeMetadataForm: FC<CodeMetadataFormProps> = ({ codeMetadata, codeId, open, refetch, onClose }) => {
  const createMutation = useMutateCreateCodeMetaData();
  const updateMutation = useMutateUpdateCodeMetaData();
  const formattedData: CodeMetadataFormValues = useMemo(() => {
    if (codeMetadata) {
      return {
        prompt: codeMetadata.prompt,
        inputType: codeMetadata.inputType,
      };
    }
    return defaultValues;
  }, [codeMetadata]);

  return (
    <FormDialog
      open={open}
      name={'code-metadata'}
      onClose={onClose}
      data={formattedData}
      schema={codeMetadataFormSchema}
      isEditing={!!codeMetadata}
      onSubmit={async (data, isEditing) => {
        if (isEditing && codeMetadata) {
          await updateMutation.mutateAsync({
            prompt: data.prompt,
            inputType: data.inputType,
            id: codeMetadata.id,
          });
          track(Events.CodeMetadataUpdate, {
            codeMetadatumId: codeMetadata.id,
          });
        } else if (codeId){
          const response = await createMutation.mutateAsync({
            prompt: data.prompt,
            inputType: data.inputType,
            codeId,
          });
          track(Events.CodeMetadataCreation, {
            codeMetadatumId: response.id,
          });
        }
      }}
      refetch={refetch}
    >
      {(control, errors) => (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name={'prompt'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.prompt}
                helperText={errors?.prompt?.message || ''}
                label={'Prompt'}
              />
            )}
          />
          <Controller
            name={'inputType'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  {...field}
                  value={field.value}
                  onChange={(_: any, value) => field.onChange(value)}
                  id={'input-type-select'}
                  options={INPUT_TYPE_OPTIONS}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionLabel={(inputType) => humanize(inputType)}
                  renderInput={(params) => <TextField {...params} label={'Input Type'} />}
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={option} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
        </Stack>
      )}
    </FormDialog>
  );
};

export default CodeMetadataForm;
